/**
 * Pre-flight questionnaire → deterministic scaffold request.
 *
 * The wizard collects 5 answers; this module maps them to an exact
 * archetype + theme + params payload that the existing buildScaffoldTool
 * can execute. No LLM in the path — answers are the contract.
 *
 * Step 1 of the dashboard-quality refactor (Move 5).
 */

export type Industry =
  | 'healthcare'
  | 'finance'
  | 'listed_board'
  | 'other';

export type Purpose =
  | 'board_pack'
  | 'monthly_pl'
  | 'annual_pl_vs_py'
  | 'variance'
  | 'cashflow'
  | 'revenue_breakdown'
  | 'operations'
  | 'kpi_scorecard'
  | 'department_compare'
  | 'compliance';

export type Granularity = 'monthly' | 'quarterly' | 'yearly' | 'mixed';

export interface WizardAnswers {
  industry: Industry;
  purpose: Purpose;
  granularity: Granularity;
  kpiCount: number;
  includeCommentary: boolean;
  /** Move 5 Step 5 — render an on-sheet audit panel with KPI traceability. */
  includeAuditPanel: boolean;
}

export interface ScaffoldRequest {
  archetype: string;
  theme: string;
  kpiCount?: number;
  topN?: number;
  includeCommentary?: boolean;
  includeAuditPanel?: boolean;
}

const PURPOSE_TO_ARCHETYPE: Record<Purpose, string> = {
  board_pack: 'board_pack_summary',
  monthly_pl: 'board_pl_monthly',
  annual_pl_vs_py: 'annual_pl_compare',
  variance: 'pl_variance_monthly',
  cashflow: 'cash_forecast',
  revenue_breakdown: 'revenue_breakdown',
  operations: 'operations_dashboard',
  kpi_scorecard: 'executive_kpi_scorecard',
  department_compare: 'department_compare',
  compliance: 'compliance_risk_register',
};

const INDUSTRY_TO_THEME: Record<Industry, string> = {
  healthcare: 'healthcare_clean',
  finance: 'financial_trust',
  listed_board: 'board_slate',
  other: 'corporate_navy',
};

export function questionnaireToScaffold(answers: WizardAnswers): ScaffoldRequest {
  const archetype = PURPOSE_TO_ARCHETYPE[answers.purpose];
  const theme = INDUSTRY_TO_THEME[answers.industry];

  const req: ScaffoldRequest = {
    archetype,
    theme,
    kpiCount: clampKpiCount(answers.kpiCount),
    includeCommentary: answers.includeCommentary,
    includeAuditPanel: answers.includeAuditPanel,
  };

  // Revenue-breakdown archetype uses topN, not kpiCount.
  if (archetype === 'revenue_breakdown') {
    req.topN = clampKpiCount(answers.kpiCount);
  }

  return req;
}

function clampKpiCount(n: number): number {
  if (!Number.isFinite(n)) return 6;
  return Math.max(4, Math.min(8, Math.round(n)));
}

export const PURPOSE_LABELS: Array<{ value: Purpose; label: string; hint: string }> = [
  { value: 'board_pack', label: 'Board pack cover', hint: '12 headline tiles + sparklines + mini trends' },
  { value: 'monthly_pl', label: 'Monthly P&L', hint: 'KPI tiles, period selector, revenue/expense tables, heatmap, charts' },
  { value: 'annual_pl_vs_py', label: 'Annual P&L vs prior year', hint: 'CY-vs-PY KPI summary + 4 charts in 2×2 grid' },
  { value: 'variance', label: 'Variance / budget vs actual', hint: 'Monthly P&L variance with conditional formatting' },
  { value: 'cashflow', label: 'Cashflow / runway', hint: 'Opening, closing, net tiles + waterfall + heatmap' },
  { value: 'revenue_breakdown', label: 'Sales / revenue breakdown', hint: 'Top-N tiles + segment table + donut + stacked column' },
  { value: 'operations', label: 'Operations dashboard', hint: 'Non-financial KPIs (occupancy, utilisation, headcount) with variance arrows' },
  { value: 'kpi_scorecard', label: 'Executive KPI scorecard', hint: '3 headline gauges + 4×2 KPI tiles + compact P&L' },
  { value: 'department_compare', label: 'Department comparison', hint: 'Up to 6 sheets side-by-side with heatmap + clustered chart' },
  { value: 'compliance', label: 'Risk / compliance register', hint: 'Status tiles + 5×5 likelihood-impact heatmap + register' },
];

export const INDUSTRY_LABELS: Array<{ value: Industry; label: string }> = [
  { value: 'other', label: 'General / SME' },
  { value: 'healthcare', label: 'Healthcare / NDIS / aged care' },
  { value: 'finance', label: 'Banking / financial services' },
  { value: 'listed_board', label: 'Listed / Tier 1 / board context' },
];

export const GRANULARITY_LABELS: Array<{ value: Granularity; label: string }> = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
  { value: 'mixed', label: 'Mixed' },
];

export const DEFAULT_ANSWERS: WizardAnswers = {
  industry: 'other',
  purpose: 'monthly_pl',
  granularity: 'monthly',
  kpiCount: 6,
  includeCommentary: false,
  includeAuditPanel: false,
};

/**
 * Formats a wizard run as a synthetic user-bubble prompt so the chat
 * history shows what the user actually asked for. The text deliberately
 * names the archetype + theme + KPI count — that's what would have taken
 * 200+ tokens for the agent to figure out otherwise.
 */
export function formatWizardPrompt(answers: WizardAnswers, archetype: string): string {
  const purposeLabel = PURPOSE_LABELS.find(p => p.value === answers.purpose)?.label ?? answers.purpose;
  const industryLabel = INDUSTRY_LABELS.find(i => i.value === answers.industry)?.label ?? answers.industry;
  const granularityLabel = GRANULARITY_LABELS.find(g => g.value === answers.granularity)?.label.toLowerCase() ?? answers.granularity;
  return [
    `Build dashboard via wizard:`,
    `  Purpose: ${purposeLabel} → archetype \`${archetype}\``,
    `  Industry: ${industryLabel}`,
    `  Granularity: ${granularityLabel}`,
    `  KPI tiles: ${answers.kpiCount}`,
    `  Commentary: ${answers.includeCommentary ? 'yes' : 'no'}`,
    `  Audit panel: ${answers.includeAuditPanel ? 'yes' : 'no'}`,
  ].join('\n');
}

/**
 * Formats the wizard build result as an assistant-bubble markdown reply.
 * Matches the existing chat presentation: tool chip plus a 1-2 line
 * summary, plus any validation issues honestly surfaced.
 */
export function formatWizardResultMarkdown(result: {
  ok: boolean;
  archetype: string;
  opsCompleted?: number;
  durationMs?: number;
  validation?: {
    passed: boolean;
    issues: Array<{ type: string; message?: string; sheet?: string; range?: string; errorToken?: string }>;
    charts_expected?: number;
    charts_found?: number;
    /** Move 5 Step 3 */
    named_ranges_expected?: number;
    named_ranges_found?: number;
    formula_errors_found?: number;
  };
  build_complete?: boolean;
  warnings?: string[];
  error?: string;
  errorDetails?: { op_index?: number; op_type?: string; error_code?: string };
  commentary?: { ok: boolean; paragraphsWritten?: number; modelId?: string; durationMs?: number; warnings?: string[]; error?: string };
}): string {
  if (!result.ok) {
    const detail = result.errorDetails?.op_type
      ? ` (op[${result.errorDetails.op_index}]: \`${result.errorDetails.op_type}\` — \`${result.errorDetails.error_code ?? 'unknown'}\`)`
      : '';
    return `**Wizard build failed.** ${result.error ?? 'unknown error'}${detail}`;
  }
  const lines: string[] = [];
  const passed = result.build_complete === true;
  const headline = passed
    ? `**Built \`${result.archetype}\`** — ${result.opsCompleted ?? '?'} ops in ${result.durationMs ?? '?'} ms.`
    : `**Built \`${result.archetype}\`** with validation issues — ${result.opsCompleted ?? '?'} ops in ${result.durationMs ?? '?'} ms.`;
  lines.push(headline);
  if (result.validation) {
    const v = result.validation;
    const chunks: string[] = [];
    if (typeof v.charts_expected === 'number' && typeof v.charts_found === 'number') {
      chunks.push(`Charts: ${v.charts_found}/${v.charts_expected}`);
    }
    if (typeof v.named_ranges_expected === 'number' && typeof v.named_ranges_found === 'number' && v.named_ranges_expected > 0) {
      chunks.push(`Named ranges: ${v.named_ranges_found}/${v.named_ranges_expected}`);
    }
    if (typeof v.formula_errors_found === 'number' && v.formula_errors_found > 0) {
      chunks.push(`Formula errors: ${v.formula_errors_found}`);
    }
    chunks.push(`Validation: ${v.passed ? 'passed ✓' : `${v.issues.length} issue(s)`}`);
    lines.push(chunks.join(' · ') + '.');
    if (!v.passed && v.issues.length) {
      lines.push('');
      lines.push('Issues:');
      for (const issue of v.issues.slice(0, 5)) {
        const where = issue.sheet
          ? ` ${issue.sheet}${issue.range ? `!${issue.range}` : ''}`
          : '';
        const detail = issue.errorToken ? ` (${issue.errorToken})` : '';
        lines.push(`- \`${issue.type}\`${where}${detail}`);
      }
      if (v.issues.length > 5) lines.push(`- … and ${v.issues.length - 5} more`);
    }
  }
  if (result.warnings && result.warnings.length) {
    lines.push('');
    lines.push('Warnings from builder:');
    for (const w of result.warnings.slice(0, 3)) lines.push(`- ${w}`);
  }
  if (result.commentary) {
    lines.push('');
    if (result.commentary.ok) {
      const para = result.commentary.paragraphsWritten ?? 0;
      const ms = result.commentary.durationMs;
      lines.push(`**Commentary:** ${para} paragraph${para === 1 ? '' : 's'} written by Sonnet${typeof ms === 'number' ? ` in ${ms} ms` : ''}.`);
    } else {
      lines.push(`**Commentary:** skipped — ${result.commentary.error ?? 'unknown error'}.`);
    }
  }
  return lines.join('\n');
}
